"use client";

import { useState } from "react";
import Link from "next/link";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import AnimatedLogo from "./AnimatedLogo";
import { AuthButtons } from "./auth/AuthButtons";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/hooks/useRole";
import { ChevronDown, Menu, X } from "lucide-react";
import { IpStatusBanner } from "./IpStatusBanner";

const Navbar = () => {
  const navbar = useQuery(api.navbar.getNavbar);
  const { isAuthenticated } = useAuth();
  const { isAdmin } = useRole();
  const settings = useQuery(api.maintenanceMode.getMaintenanceMode);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileAccordionOpen, setMobileAccordionOpen] = useState<number | null>(null);

  // Default values while loading or if no navbar config exists
  const logoText = navbar?.logo.text || "Tiera";
  const logoHref = navbar?.logo.href || "/";

  // Filter to get only top-level links (children are nested within parent links)
  const topLevelLinks = (navbar?.links || [
    { label: "Download", href: "/download", order: 0 },
    { label: "Pricing", href: "/pricing", order: 1 },
  ]).sort((a, b) => a.order - b.order);

  const ctaButton = navbar?.ctaButton || { label: "Get Tiera VPN", href: "/download" };

  // Check if maintenance banner is showing (admin viewing during maintenance mode)
  const showMaintenanceBanner = isAdmin && (settings?.comingSoonEnabled || settings?.maintenanceEnabled);
  // Account for IP banner (32px) + maintenance banner (32px if showing)
  const topOffset = showMaintenanceBanner ? "top-16" : "top-8";

  return (
    <>
      <IpStatusBanner />
      <nav className={`fixed left-0 right-0 z-50 bg-white transition-all duration-300 ${topOffset}`}>
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between py-3">
        <Link href={logoHref} className="flex items-center gap-2 flex-shrink-0">
          <AnimatedLogo width={44} height={44} className="w-11 h-auto" />
          <span className="text-xl font-black tracking-tight text-foreground uppercase">
            {logoText}
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-10 text-[13px] font-bold text-foreground/70 tracking-wide">
          {topLevelLinks.map((link, index) => {
            const hasChildren = link.children && link.children.length > 0;

            // If link has children, render as dropdown
            if (hasChildren) {
              return (
                <div
                  key={index}
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(index)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  {/* Parent link - clickable if it has href, otherwise just triggers dropdown */}
                  {link.href ? (
                    <Link
                      href={link.href}
                      className="flex items-center gap-1 hover:text-primary transition-colors"
                      {...(link.href.startsWith('http') ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    >
                      {link.label}
                      <ChevronDown className="h-3 w-3" />
                    </Link>
                  ) : (
                    <button
                      className="flex items-center gap-1 hover:text-primary transition-colors"
                    >
                      {link.label}
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  )}

                  {/* Dropdown menu */}
                  <div
                    className={`absolute top-full left-0 mt-2 min-w-[200px] bg-white rounded-lg shadow-lg border border-border overflow-hidden transition-all duration-200 ${
                      openDropdown === index
                        ? "opacity-100 visible translate-y-0"
                        : "opacity-0 invisible -translate-y-2"
                    }`}
                  >
                    {link.children?.map((child, childIndex) => (
                      <Link
                        key={childIndex}
                        href={child.href}
                        className="block px-4 py-3 text-sm text-foreground/80 hover:bg-muted hover:text-primary transition-colors"
                        {...(child.href.startsWith('http') ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            }

            // Regular link without children
            if (link.href) {
              return (
                <Link
                  key={index}
                  href={link.href}
                  className="hover:text-primary transition-colors"
                  {...(link.href.startsWith('http') ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                >
                  {link.label}
                </Link>
              );
            }

            // Fallback for links without href and without children (shouldn't happen)
            return null;
          })}
        </div>

        <div className="flex items-center gap-6">
          {/* Desktop auth and CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <AuthButtons />
            {!isAuthenticated && (
              <Link
                href={ctaButton.href}
                className="px-6 py-2.5 rounded-xl bg-primary text-white text-[13px] font-bold hover:bg-foreground transition-all shadow-lg shadow-primary/20"
              >
                {ctaButton.label}
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 text-foreground/70 hover:text-primary transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden bg-white border-t border-border transition-all duration-300 ease-in-out ${
          mobileMenuOpen
            ? "max-h-screen opacity-100"
            : "max-h-0 opacity-0 overflow-hidden"
        }`}
      >
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-4 space-y-2">
          {topLevelLinks.map((link, index) => {
            const hasChildren = link.children && link.children.length > 0;

            if (hasChildren) {
              const isOpen = mobileAccordionOpen === index;

              return (
                <div key={index}>
                  {/* Parent link with accordion toggle */}
                  <div className="flex items-center justify-between">
                    {link.href ? (
                      <Link
                        href={link.href}
                        className="flex-1 py-3 text-sm font-bold text-foreground/80 hover:text-primary transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                        {...(link.href.startsWith('http') ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <span className="flex-1 py-3 text-sm font-bold text-foreground/80">
                        {link.label}
                      </span>
                    )}

                    <button
                      onClick={() => setMobileAccordionOpen(isOpen ? null : index)}
                      className="p-2 text-foreground/70 hover:text-primary transition-colors"
                      aria-label={`Toggle ${link.label} submenu`}
                    >
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  </div>

                  {/* Accordion content */}
                  <div
                    className={`overflow-hidden transition-all duration-200 ${
                      isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="pl-4 pb-2 space-y-1">
                      {link.children?.map((child, childIndex) => (
                        <Link
                          key={childIndex}
                          href={child.href}
                          className="block py-2 text-sm text-foreground/70 hover:text-primary transition-colors"
                          onClick={() => setMobileMenuOpen(false)}
                          {...(child.href.startsWith('http') ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            // Regular link without children
            if (link.href) {
              return (
                <Link
                  key={index}
                  href={link.href}
                  className="block py-3 text-sm font-bold text-foreground/80 hover:text-primary transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                  {...(link.href.startsWith('http') ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                >
                  {link.label}
                </Link>
              );
            }

            return null;
          })}

          {/* Mobile auth - Log in as list item or UserMenu if authenticated */}
          <div className="py-3">
            <AuthButtons variant="mobile" />
          </div>

          {/* Mobile CTA button at bottom with separator - only show when not authenticated */}
          {!isAuthenticated && (
            <div className="border-t border-border pt-4 mt-2">
              <Link
                href={ctaButton.href}
                className="block w-full px-6 py-2.5 rounded-xl bg-primary text-white text-[13px] font-bold hover:bg-foreground transition-all shadow-lg shadow-primary/20 text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                {ctaButton.label}
              </Link>
            </div>
          )}
        </div>
      </div>
    </nav>
    </>
  );
};

export default Navbar;
